import safeStorage from '@github-ui/safe-storage'

export type Listener = () => void

// Global registry of all store caches for test cleanup
// Exposed on window so tests can clear without importing (avoids vi.mock issues)
const allCaches: Array<Map<string, unknown>> = []

declare global {
  interface Window {
    __SAFE_STORAGE_CLEAR_ALL_CACHES__?: () => void
  }
}

// Register global cleanup function
if (typeof window !== 'undefined') {
  window.__SAFE_STORAGE_CLEAR_ALL_CACHES__ = () => {
    for (const cache of allCaches) {
      cache.clear()
    }
  }
}

export const createStore = (storageType: 'localStorage' | 'sessionStorage') => {
  const storage = safeStorage(storageType)

  const cache = new Map<string, unknown>()
  const listeners = new Map<string, Set<Listener>>()

  // Register this cache for global cleanup
  allCaches.push(cache)

  function isEqual<T>(a: T, b: T): boolean {
    if (a === b) return true
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      // Circular references or other non-serializable values
      return false
    }
  }

  function emit(key: string): void {
    const set = listeners.get(key)
    if (set) {
      for (const listener of set) {
        listener()
      }
    }
  }

  function subscribe(key: string, listener: Listener): () => void {
    let set = listeners.get(key)
    if (!set) {
      set = new Set()
      listeners.set(key, set)
    }
    set.add(listener)
    return () => {
      set.delete(listener)
      // Clean up empty listener sets to prevent memory leaks
      if (set.size === 0) {
        listeners.delete(key)
      }
    }
  }

  function readStorage<T>(key: string, initial: T): T {
    // Always return cached value if present - critical for useSyncExternalStore contract
    if (cache.has(key)) {
      return cache.get(key) as T
    }

    try {
      const raw = storage.getItem(key)
      const value = raw == null ? initial : JSON.parse(raw)
      cache.set(key, value)
      return value
    } catch {
      cache.set(key, initial)
      return initial
    }
  }

  function writeStorage<T>(key: string, value: T): void {
    const prev = cache.get(key)
    if (isEqual(prev, value)) return

    // Update cache and notify subscribers
    cache.set(key, value)
    emit(key)

    // safeStorage handles quota errors internally
    storage.setItem(key, JSON.stringify(value))
  }

  /**
   * Remove a key from storage and cache, notifying subscribers
   */
  function removeStorage(key: string): void {
    cache.delete(key)
    storage.removeItem(key)
    emit(key)
  }

  /**
   * Reset a key to a default value atomically - updates cache before emitting
   * to avoid timing issues with useSyncExternalStore snapshots
   */
  function resetStorage<T>(key: string, defaultValue: T): void {
    const prev = cache.get(key)
    if (isEqual(prev, defaultValue)) return

    cache.set(key, defaultValue) // Set default BEFORE emit
    storage.removeItem(key)
    emit(key)
  }

  /**
   * Clear all cached data (useful for testing).
   * Emits to subscribers - components should handle cache miss in getSnapshot
   * by reseeding with their initialValue.
   */
  function clearAll(): void {
    const keys = [...cache.keys()]
    for (const key of keys) {
      cache.delete(key)
      storage.removeItem(key)
      emit(key)
    }
  }

  /**
   * Clear specified keys from cache and storage.
   * Emits to subscribers - components should handle cache miss in getSnapshot
   * by reseeding with their initialValue.
   */
  function clearKeys(keys: string[]): void {
    for (const key of keys) {
      cache.delete(key)
      storage.removeItem(key)
      emit(key)
    }
  }

  /**
   * Clear keys matching a prefix from cache and storage.
   * Emits to subscribers - components should handle cache miss in getSnapshot
   * by reseeding with their initialValue.
   */
  function clearKeysByPrefix(prefix: string): void {
    const keysToRemove = storage.getKeys().filter(key => key.startsWith(prefix))
    for (const key of keysToRemove) {
      cache.delete(key)
      storage.removeItem(key)
      emit(key)
    }
  }

  /**
   * Get all items whose keys start with a prefix
   */
  function getItemsByPrefix<T>(prefix: string): T[] {
    const items: T[] = []
    for (const key of storage.getKeys()) {
      if (key.startsWith(prefix)) {
        const raw = storage.getItem(key)
        if (raw != null) {
          try {
            items.push(JSON.parse(raw) as T)
          } catch {
            // Skip invalid JSON
          }
        }
      }
    }
    return items
  }

  return {
    writeStorage,
    readStorage,
    removeStorage,
    resetStorage,
    subscribe,
    cache,
    clearAll,
    clearKeys,
    clearKeysByPrefix,
    getItemsByPrefix,
  }
}
